import React, { useEffect, useState } from 'react'
import { Classes, Tag, Tree, TreeNodeInfo } from '@blueprintjs/core'
import { isEmpty } from '../../util/helpers'
import { useTreeFunctions } from '../../hooks/useTreeFunctions'
import { KeywordMatchTypes } from '../../config'
import { capitalCase } from 'change-case'
import { Link } from 'react-router-dom'
import { money } from '../../util/formatters'
import {
    PayloadType,
    PromoDryRunKeyword,
    PromoDryRunNegativeKeyword,
} from '../../types/keywords_tree'

export const generateMatchType = (matchType: any) => {
    if (matchType == '' || matchType == null || !matchType) return ''

    let newMatchType = ''
    if (!isNaN(matchType)) {
        switch (matchType) {
            case KeywordMatchTypes.Exact:
                newMatchType = 'exact'
                break
            case KeywordMatchTypes.Phrase:
                newMatchType = 'phrase'
                break
            case KeywordMatchTypes.Broad:
                newMatchType = 'broad'
                break
        }
    }

    newMatchType = capitalCase(newMatchType).toLowerCase()
    switch (newMatchType) {
        case 'negative exact':
        case 'exact':
            return (
                <Tag style={{ display: 'inline-flex' }} intent="success">
                    <b>{newMatchType}</b>
                </Tag>
            )
        case 'negative broad':
        case 'broad':
            return (
                <Tag style={{ display: 'inline-flex' }} intent="warning">
                    <b>{newMatchType}</b>
                </Tag>
            )
        case 'negative phrase':
        case 'phrase':
            return (
                <Tag style={{ display: 'inline-flex' }} intent={'primary'}>
                    <b>{newMatchType}</b>
                </Tag>
            )
        default:
            return newMatchType
    }
}

export interface TreeProps<T> {
    data: PayloadType<T>
    title: string
    showAdGroup?: boolean
    bid?: boolean
}

interface Item {
    matchType: string
    keywordText: string
    bid: number | null
    adGroupName: string
    campaignName: string
    campaignId: number
    adGroupId: number
}

type Keywords = { [key: string]: Item[] }

export const KeywordsTree = <T extends PromoDryRunKeyword | PromoDryRunNegativeKeyword>({
    data,
    title,
    showAdGroup = true,
    bid = true,
}: TreeProps<T>): JSX.Element => {
    const [keywords, setKeywords] = useState<Keywords>({})
    const { nodes, setNodes, handleNodeClick, handleNodeCollapse, handleNodeExpand } =
        useTreeFunctions()

    const load = () => {
        if (data.items == null || data.count == 0) return

        const output: Keywords = {}
        data.items.forEach((item) => {
            const createdItem: Item = {
                matchType: item.matchType,
                keywordText: item.keywordText,
                bid: 'bid' in item ? item.bid : null,
                adGroupName: item.adGroupData?.name,
                campaignName: item.campaignData.name,
                campaignId: item.campaignId,
                adGroupId: item.adGroupId,
            }

            const key = item.campaignId + ':' + item.adGroupId
            if (output.hasOwnProperty(key)) {
                output[key].push(createdItem)
            } else {
                output[key] = [createdItem]
            }
        })
        setKeywords(output)
    }

    const generateLabel = (items: Item[]) => {
        const campaignLink = (
            <Link to={'/campaigns/' + items[0].campaignId}>{items[0].campaignName}</Link>
        )
        const adGroupLink = (
            <Link to={'/ad-groups/' + items[0].adGroupId}>{items[0].adGroupName}</Link>
        )

        return (
            <p>
                {items.length == data.count ? 'All' : items.length} for Campaign {campaignLink}
                {showAdGroup && ', '}
                {showAdGroup ? adGroupLink : ''}
            </p>
        )
    }

    useEffect(() => {
        if (!isEmpty(data)) {
            load()
        }
    }, [data])

    useEffect(() => {
        renderKeywordTree()
    }, [keywords])

    const renderKeywordTree = () => {
        const output: TreeNodeInfo[] = []

        Object.keys(keywords).forEach((key) => {
            const items = keywords[key]

            const currentKey = output.length
            const childNodes: TreeNodeInfo[] = []
            items.forEach((item, i) => {
                childNodes.push({
                    id: currentKey + '-' + i,
                    hasCaret: false,
                    isExpanded: true,
                    label: (
                        <span>
                            {generateMatchType(item.matchType)}{' '}
                            <span>
                                {' '}
                                - {item.keywordText}
                                {bid && <span> - bid {money(item.bid)}</span>}
                            </span>
                        </span>
                    ),
                })
            })

            output.push({
                id: key,
                hasCaret: true,
                label: generateLabel(items),
                isExpanded: true,
                childNodes,
            })
        })

        setNodes(output)
    }

    return (
        <div className={'flex justify-center'}>
            <div
                className={Classes.ELEVATION_0 + ' p-4 dry-run rounded shadow bg-white'}
                style={{ maxWidth: 1200, minWidth: 1200 }}
            >
                <h2 className="text-xl font-bold pb-2">{title}</h2>
                <Tree
                    onNodeClick={handleNodeClick}
                    onNodeCollapse={handleNodeCollapse}
                    onNodeExpand={handleNodeExpand}
                    contents={nodes}
                />
            </div>
        </div>
    )
}
